import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    allowCarStatusCounterResetWithLimit,
    generateLicense,
    getLicenses,
    sendExistingLicenseViaEmail,
    updateLicense,
} from '../../core/api/endpoints/licences/licensesApi';
import { GenerateLicenseModel } from '../../core/domainModels/licences/GenerateLicense';
import { LicenseModel } from '../../core/domainModels/licences/LicenseModel';
import { UpdateLicenseModel } from '../../core/domainModels/licences/UpdateLicense';
import {
    addLicenseVersion,
    removeLicenseVersion,
    updateLicenseVersion,
} from '../../core/api/endpoints/licenceVersions/licenseVersionsApi';
import { AddLicenseVersion } from '../../core/domainModels/licenceVersions/AddLicenseVersion';
import { UpdateLicenseVersion } from '../../core/domainModels/licenceVersions/UpdateLicenseVersion';
import { TachoColumnState } from '../../core/domainModels/displayPreferences/tachoColumnState';
import { PersistConfig } from 'redux-persist';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';
import storage from 'redux-persist/es/storage';
import { fieldName } from '../../core/helpers/aggrid';

export interface LicencesState {
    licences: Array<LicenseModel>;
    fetchLicencePending: boolean;
    fetchLicenceError: string;
    getLicenceTokenPending: boolean;
    getLicenceTokenError: string;
    generateLicencePending: boolean;
    generateLicenceError: string;
    updateLicencePending: boolean;
    updateLicenceError: string;
    sendLicenceViaEmailPending: boolean;
    sendLicenceViaEmailError: string;
    allowCarStatusCounterResetPending: boolean;
    allowCarStatusCounterResetError: string;
    licenceVersionPending: boolean;
    licenceVersionError: string;
    displayPreferences: Array<TachoColumnState>;
    tableFilters: any;
}

const initialState: LicencesState = {
    licences: [],
    fetchLicencePending: false,
    fetchLicenceError: '',
    getLicenceTokenPending: false,
    getLicenceTokenError: '',
    generateLicencePending: false,
    generateLicenceError: '',
    updateLicencePending: false,
    updateLicenceError: '',
    sendLicenceViaEmailPending: false,
    sendLicenceViaEmailError: '',
    allowCarStatusCounterResetPending: false,
    allowCarStatusCounterResetError: '',
    licenceVersionPending: false,
    licenceVersionError: '',
    displayPreferences: [],
    tableFilters: {},
};

const name = 'licences';

const allowCarStatusCounterResetThunk = createAsyncThunk(
    `${name}/allowCarStatusCounterResetWithLimit`,
    async (params: { licenceNumber: string; limit: number }, thunkAPI) => {
        try {
            return await allowCarStatusCounterResetWithLimit(params.licenceNumber, params.limit);
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

const fetchLicenceThunk = createAsyncThunk(`${name}/fetch`, async (params, thunkAPI) => {
    try {
        return await getLicenses();
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const generateLicenceThunk = createAsyncThunk(`${name}/generate`, async (licence: GenerateLicenseModel, thunkAPI) => {
    try {
        return await generateLicense(licence);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const updateLicenceThunk = createAsyncThunk(
    `${name}/sendLicenceViaEmail`,
    async (params: { licence: UpdateLicenseModel; licenseNumber: string }, thunkAPI) => {
        try {
            return await updateLicense(params.licence, params.licenseNumber);
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

const sendExistingLicenceViaEmailThunk = createAsyncThunk(`${name}/update`, async (licenceNumber: string, thunkAPI) => {
    try {
        return await sendExistingLicenseViaEmail(licenceNumber);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const updateLicenceVersionThunk = createAsyncThunk(
    `${name}/version/update`,
    async (params: { updatedLicenceVersion: UpdateLicenseVersion; licenseNumber: string }, thunkAPI) => {
        try {
            return await updateLicenseVersion(params.updatedLicenceVersion, params.licenseNumber);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    },
);

const removeLicenceVersionThunk = createAsyncThunk(
    `${name}/version/remove`,
    async (params: { licenseVersionId: string; licenseNumber: string }, thunkAPI) => {
        try {
            return await removeLicenseVersion(params.licenseVersionId, params.licenseNumber);
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

const addLicenceVersionThunk = createAsyncThunk(`${name}/version/add`, async (newLicence: AddLicenseVersion, thunkAPI) => {
    try {
        return await addLicenseVersion(newLicence);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const licencesSlice = createSlice({
    name,
    initialState,
    reducers: {
        modifyLicencesDisplayPreferences(state, action: PayloadAction<any>) {
            state.displayPreferences = action.payload;
        },
        modifyLicencesTableFilters(state, action: PayloadAction<any>) {
            state.tableFilters = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchLicenceThunk.pending, (state, action) => {
                state.fetchLicencePending = true;
                state.fetchLicenceError = '';
            })
            .addCase(fetchLicenceThunk.fulfilled, (state, action) => {
                state.fetchLicenceError = '';
                state.licences = action.payload;
                state.fetchLicencePending = false;
            })
            .addCase(fetchLicenceThunk.rejected, (state, action) => {
                state.fetchLicencePending = false;
                state.fetchLicenceError = action.payload as string;
            })
            .addCase(updateLicenceThunk.pending, (state, action) => {
                state.updateLicencePending = true;
                state.updateLicenceError = '';
            })
            .addCase(updateLicenceThunk.fulfilled, (state, action) => {
                const updatedLicenceIndex = state.licences.findIndex(c => c.licenseNumber === action.payload.licenseNumber);
                if (updatedLicenceIndex !== -1) {
                    state.licences[updatedLicenceIndex] = { ...state.licences[updatedLicenceIndex], ...action.payload };
                }
                state.updateLicencePending = false;
            })
            .addCase(updateLicenceThunk.rejected, (state, action) => {
                state.updateLicencePending = false;
                state.updateLicenceError = action.payload as string;
            })
            .addCase(generateLicenceThunk.pending, (state, action) => {
                state.generateLicencePending = true;
                state.generateLicenceError = '';
            })
            .addCase(generateLicenceThunk.fulfilled, (state, action) => {
                state.generateLicenceError = '';
                state.generateLicencePending = false;
            })
            .addCase(generateLicenceThunk.rejected, (state, action) => {
                state.generateLicencePending = false;
                state.generateLicenceError = action.payload as string;
            })
            .addCase(sendExistingLicenceViaEmailThunk.pending, (state, action) => {
                state.sendLicenceViaEmailPending = true;
                state.sendLicenceViaEmailError = '';
            })
            .addCase(sendExistingLicenceViaEmailThunk.fulfilled, (state, action) => {
                state.sendLicenceViaEmailError = '';
                state.sendLicenceViaEmailPending = false;
            })
            .addCase(sendExistingLicenceViaEmailThunk.rejected, (state, action) => {
                state.sendLicenceViaEmailPending = false;
                state.sendLicenceViaEmailError = action.payload as string;
            })
            .addCase(allowCarStatusCounterResetThunk.fulfilled, (state, action) => {
                state.allowCarStatusCounterResetError = '';
                state.allowCarStatusCounterResetPending = false;
            })
            .addCase(allowCarStatusCounterResetThunk.rejected, (state, action) => {
                state.allowCarStatusCounterResetPending = false;
                state.allowCarStatusCounterResetError = action.payload as string;
            })
            .addCase(addLicenceVersionThunk.pending, (state, action) => {
                state.licenceVersionPending = true;
                state.licenceVersionError = '';
            })
            .addCase(addLicenceVersionThunk.fulfilled, (state, action) => {
                state.licenceVersionError = '';
                state.licenceVersionPending = false;
            })
            .addCase(addLicenceVersionThunk.rejected, (state, action) => {
                state.licenceVersionPending = false;
                state.licenceVersionError = action.payload as string;
            })
            .addCase(updateLicenceVersionThunk.pending, (state, action) => {
                state.licenceVersionPending = true;
                state.licenceVersionError = '';
            })
            .addCase(updateLicenceVersionThunk.fulfilled, (state, action) => {
                state.licenceVersionError = '';
                state.licenceVersionPending = false;
            })
            .addCase(updateLicenceVersionThunk.rejected, (state, action) => {
                state.licenceVersionPending = false;
                state.licenceVersionError = action.payload as string;
            })
            .addCase(removeLicenceVersionThunk.pending, (state, action) => {
                state.licenceVersionPending = true;
                state.licenceVersionError = '';
            })
            .addCase(removeLicenceVersionThunk.fulfilled, (state, action) => {
                state.licenceVersionError = '';
                state.licenceVersionPending = false;
            })
            .addCase(removeLicenceVersionThunk.rejected, (state, action) => {
                state.licenceVersionPending = false;
                state.licenceVersionError = action.payload as string;
            });
    },
});

const { reducer, actions } = licencesSlice;
export const licencesReducerConfig: PersistConfig<LicencesState> = {
    key: name,
    storage: storage,
    stateReconciler: autoMergeLevel1,
    whitelist: [fieldName<LicencesState>('displayPreferences'), fieldName<LicencesState>('tableFilters')],
};
export { reducer as licencesReducer };

const { modifyLicencesDisplayPreferences, modifyLicencesTableFilters } = actions;
export {
    fetchLicenceThunk,
    generateLicenceThunk,
    updateLicenceThunk,
    sendExistingLicenceViaEmailThunk,
    allowCarStatusCounterResetThunk,
    updateLicenceVersionThunk,
    addLicenceVersionThunk,
    removeLicenceVersionThunk,
    modifyLicencesDisplayPreferences,
    modifyLicencesTableFilters,
};
